import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  verseKeyOf, verseSelector, headerInset, needsFollow, followScrollTop, isUserScroll,
} from './follow';

// Panels that scroll by themselves: a wheel or drag inside them moves the
// panel, not the page, so it says nothing about following.
const INNER_SCROLLERS = [
  '.daily-bread .db-body', '.bible-ref-popup', '.mobile-sheet', '.search-overlay',
  '.anno-note-editor', '.bible-chapter-grid', '.bible-verse-jump-strip',
  '.notif-panel', '.account-panel', 'select', 'textarea',
].join(',');

// Focused controls that use the arrow keys themselves (an audio element
// seeks with them), and those that also take the space bar (a button presses).
const ARROW_TAKERS = 'input, textarea, select, audio, video, [contenteditable], [role="slider"], [role="listbox"]';
const SPACE_TAKERS = `${ARROW_TAKERS}, button, a[href], summary, [role="button"]`;

const MARK = 'data-verse-now';

const reducedMotion = () => !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// ── Lighting and following the verse being read ─────────────────────────────
// Each page reports what its players are doing: onVerse(source, {book,
// chapter, verse} | null) as the verse changes and onPlaying(source, bool) on
// play and pause, where `source` names the player ('nt:en', 'a', …). The
// player started last is the one followed.
//
// The verse is marked by setting data-verse-now="playing" | "paused" on every
// element under `rootRef` whose data-book / data-chapter / data-verse match,
// so both columns of a parallel page light up together. It is set straight on
// the DOM: a verse change re-renders nothing, and the attribute is one React
// never manages, so a re-render of the verse elements leaves it alone. (Any
// render of the page re-applies it anyway, for verses drawn afresh.)
//
// While playing, the page scrolls to keep the verse in the middle third of
// the space below `headerSelectors` (the sticky bars). Scrolling by hand
// stops that and shows the "Follow audio" pill (`pillVisible`; `resume`
// brings it back). Pausing stops following; pressing play again restarts it.
export function useFollowVerse(rootRef, headerSelectors) {
  const [pillVisible, setPillVisible] = useState(false);
  const stateRef = useRef(null);
  if (!stateRef.current) {
    stateRef.current = {
      active: null,            // the source being followed
      verses: new Map(),       // source → last verse it reported
      playing: new Map(),      // source → playing?
      following: true,
      followedKey: null,       // the verse last considered for scrolling
      touch: null,             // where the current touch began
    };
  }
  const headersRef = useRef(headerSelectors);
  headersRef.current = headerSelectors;

  const api = useMemo(() => {
    const st = stateRef.current;

    const current = () => {
      const v = st.active ? st.verses.get(st.active) || null : null;
      return { v, playing: !!(st.active && st.playing.get(st.active)) };
    };

    // Puts the mark on the current verse's elements (and takes it off any
    // others); returns those elements.
    function mark() {
      const root = rootRef.current;
      if (!root) return [];
      const { v, playing } = current();
      const sel = verseSelector(v);
      const state = playing ? 'playing' : 'paused';
      const els = sel ? Array.from(root.querySelectorAll(sel)) : [];
      root.querySelectorAll(`[${MARK}]`).forEach(el => {
        if (!els.includes(el)) el.removeAttribute(MARK);
      });
      els.forEach(el => { if (el.getAttribute(MARK) !== state) el.setAttribute(MARK, state); });
      return els;
    }

    // The visible part of the verse (both columns, every line of it), or
    // null when none of it is drawn — a collapsed "Read verses" box.
    function unionRect(els) {
      let r = null;
      for (const el of els) {
        if (!el.getClientRects().length) continue;
        const b = el.getBoundingClientRect();
        if (!b.height) continue;
        r = r
          ? { top: Math.min(r.top, b.top), bottom: Math.max(r.bottom, b.bottom) }
          : { top: b.top, bottom: b.bottom };
      }
      return r;
    }

    function band() {
      const rects = [];
      (headersRef.current || []).forEach(sel => {
        document.querySelectorAll(sel).forEach(el => rects.push(el.getBoundingClientRect()));
      });
      return { top: headerInset(rects, window.innerHeight), bottom: window.innerHeight };
    }

    // `force` scrolls even when the verse is already in the middle third
    // (the reader asked to go back to it).
    function scrollTo(els, force) {
      const rect = unionRect(els);
      if (!rect) return false;
      const b = band();
      if (!force && !needsFollow(rect, b)) return true;
      const top = followScrollTop(rect, b, window.scrollY);
      if (Math.abs(top - window.scrollY) < 2) return true;
      window.scrollTo({ top, behavior: reducedMotion() ? 'auto' : 'smooth' });
      return true;
    }

    // Marks the verse and, when it has just changed under a playing,
    // followed recording, scrolls to it.
    function update() {
      const els = mark();
      const { v, playing } = current();
      const key = verseKeyOf(v);
      if (key === st.followedKey) return;
      st.followedKey = key;
      if (key && playing && st.following) scrollTo(els, false);
    }

    function onVerse(source, v) {
      st.verses.set(source, v || null);
      if (!st.active && v) st.active = source;
      if (source === st.active) update();
    }

    function onPlaying(source, playing) {
      // Already playing (the next chapter starting on its own): nothing new,
      // and a reader who scrolled away stays scrolled away.
      if (playing && st.active === source && st.playing.get(source)) return;
      st.playing.set(source, !!playing);
      if (playing) {
        st.active = source;
        st.following = true;
        setPillVisible(false);
        mark();
        const { v } = current();
        st.followedKey = verseKeyOf(v);
        if (v) scrollTo(mark(), false);
      } else if (source === st.active) {
        setPillVisible(false);
        mark();
      }
    }

    // Forget everything: a new chapter, or the players went away.
    function reset() {
      st.active = null;
      st.verses.clear();
      st.playing.clear();
      st.following = true;
      st.followedKey = null;
      setPillVisible(false);
      mark();
    }

    function resume() {
      st.following = true;
      setPillVisible(false);
      scrollTo(mark(), true);
    }

    // The reader scrolled by hand while we were following: stop, and offer
    // the pill. Only when the verse is on the page at all — with the verses
    // box shut there is nothing to go back to.
    function userScrolled() {
      const { v, playing } = current();
      if (!v || !playing || !st.following) return;
      if (!unionRect(mark())) return;
      st.following = false;
      setPillVisible(true);
    }

    return { onVerse, onPlaying, reset, resume, mark, userScrolled };
  }, [rootRef]);

  // Re-apply after every render of the page, for verses drawn afresh (a
  // reopened verses box, a font change, Refs switched on).
  useLayoutEffect(() => { api.mark(); });

  // Reading the reader's own scrolling. Listened for all the time (cheap);
  // userScrolled ignores it unless audio is playing and being followed.
  useEffect(() => {
    const st = stateRef.current;
    const inner = t => !!t?.closest?.(INNER_SCROLLERS);
    const onWheel = e => {
      if (isUserScroll({ type: 'wheel', deltaY: e.deltaY, ctrlKey: e.ctrlKey, inner: inner(e.target) })) api.userScrolled();
    };
    const onTouchStart = e => {
      const t = e.touches?.[0];
      st.touch = t && e.touches.length === 1 ? { x: t.clientX, y: t.clientY, inner: inner(e.target) } : null;
    };
    const onTouchMove = e => {
      const t = e.touches?.[0];
      const s = st.touch;
      if (!t || !s) return;
      if (isUserScroll({ type: 'touchmove', dx: t.clientX - s.x, dy: t.clientY - s.y, inner: s.inner })) {
        st.touch = null;
        api.userScrolled();
      }
    };
    const onTouchEnd = () => { st.touch = null; };
    const onKeyDown = e => {
      const space = e.key === ' ' || e.key === 'Spacebar';
      const editable = !!e.target?.closest?.(space ? SPACE_TAKERS : ARROW_TAKERS);
      if (isUserScroll({ type: 'keydown', key: e.key, altKey: e.altKey, metaKey: e.metaKey, ctrlKey: e.ctrlKey, editable })) {
        api.userScrolled();
      }
    };
    // The page's scrollbar: a press on the root element beyond its content
    // width (classic scrollbars; overlay ones show no such area and are
    // dragged by touch or wheel instead).
    const onPointerDown = e => {
      const doc = document.documentElement;
      const onScrollbar = (e.target === doc || e.target === document.body) && e.clientX >= doc.clientWidth;
      if (isUserScroll({ type: 'pointerdown', onScrollbar })) api.userScrolled();
    };
    const opts = { passive: true };
    window.addEventListener('wheel', onWheel, opts);
    window.addEventListener('touchstart', onTouchStart, opts);
    window.addEventListener('touchmove', onTouchMove, opts);
    window.addEventListener('touchend', onTouchEnd, opts);
    window.addEventListener('touchcancel', onTouchEnd, opts);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('pointerdown', onPointerDown, opts);
    return () => {
      window.removeEventListener('wheel', onWheel, opts);
      window.removeEventListener('touchstart', onTouchStart, opts);
      window.removeEventListener('touchmove', onTouchMove, opts);
      window.removeEventListener('touchend', onTouchEnd, opts);
      window.removeEventListener('touchcancel', onTouchEnd, opts);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('pointerdown', onPointerDown, opts);
    };
  }, [api]);

  const resume = useCallback(() => api.resume(), [api]);
  return { onVerse: api.onVerse, onPlaying: api.onPlaying, reset: api.reset, resume, pillVisible };
}
